import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router";

import SinglePerson from "./SinglePerson";
import LoadingSpinner from "../../common/LoadingSpinner";
import { SonicDBEntryData } from "../../common/classes/SonicDBEntry";

import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getPersonData, getPerson } from "../../redux/reducers/person";
import { AuthStatus, getAuthStatus } from "../../redux/reducers/auth";
import { getLinkedPages } from "../../redux/reducers/user";

import { useMetaData } from "../../utils/useMetaData";
import { getDefaultImage } from "../../utils/GeneralUtils";
import { useGetTeamMembers } from "../../utils/useTeam";

import Video from "../../assets/video/bg-circuit.mp4";
import posterImage from "../../assets/img/profile-bg-poster.webp";

import styles from "./singleperson.module.scss";

export const SinglePersonContainer: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();
  const personData = useAppSelector(getPersonData)[0];
  const isLoggedIn = useAppSelector(getAuthStatus);
  const loggedInUserMeta = useAppSelector(getLinkedPages);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  // データフェッチの依存関係にidを直接入れると、別のメンバーに飛ぶ際にisLoadingをtrueにセットしてもfalseとして認識されて404になる
  // isLoadingをtrueにしてからフェッチさせることで404を回避する
  useEffect(() => {
    setIsLoading(true);
  }, [id]);

  // Person Data/SNS fetching Effect
  useEffect(() => {
    if (!id || !isLoading) return;

    const personPromise = dispatch(getPerson({ id: id }));

    personPromise.then(() => {
      setIsLoading(false);
    });

    return () => {
      personPromise.abort();
    };
  }, [isLoading]);

  useMetaData(
    !isLoading && personData
      ? {
          title: personData.name,
          description: personData.profile
            ? personData.profile
            : `SELeCTに掲載中の ${personData.name} さんのeスポーツプロフィールページです。`,
          imgUrl: personData.imgs?.[0]?.medium?.url ?? "",
        }
      : undefined
  );

  // PersonContainerでメンバーをフェッチしようとするとフックのエラーに抵触するので、専用のコンポーネントを作成
  type ContainerProps = {
    person: SonicDBEntryData;
  };

  const FetchContainer: React.FC<ContainerProps> = ({ person }) => {
    const { members } = useGetTeamMembers({
      // person.teamはtermデータなので、linkからチームのidを取得
      id:
        person?.team?.length && person.team[0].link
          ? person.team[0].link.split("/").pop()
          : undefined,
    });

    return (
      <SinglePerson
        id={personData.id}
        owner={personData.owner}
        ownerName={personData.ownerName}
        ownerChatDenial={personData.ownerChatDenial}
        img={
          personData.imgs?.[0]?.large?.url
            ? personData.imgs[0].large.url
            : getDefaultImage()
        }
        name={personData.name}
        game={personData.game}
        team={personData.team?.[0] && personData.team[0]}
        playerTypeName={personData.player_type?.[0]?.name}
        profile={personData.profile}
        birthplace={personData.birthplace}
        height={personData.height}
        weight={personData.weight}
        hobby={personData.hobby}
        history={personData.history}
        works={personData.works}
        twitter={personData.sns?.twitter?.toString()}
        instagram={personData.sns?.instagram?.toString()}
        facebook={personData.sns?.facebook?.toString()}
        youtube={personData.sns?.youtube?.toString()}
        twitch={personData.sns?.twitch?.toString()}
        school={personData.school}
        course={personData.course}
        verified={personData.verified}
        isLoggedIn={isLoggedIn === AuthStatus.Authenticated ? true : false}
        isUsersOwnDBEntry={
          id && loggedInUserMeta?.database_entry?.includes(id) ? true : false
        }
        showOfferButton={personData.owner ? true : false}
        members={
          // storeのデータとidが一致するときのみ自分を除いて表示
          personData.id === id
            ? members.filter((member) => member.id !== id)
            : []
        }
        gameDetails={personData.game_details ?? []}
      />
    );
  };

  return (
    <main>
      <video
        className={
          personData !== undefined && personData.id == id
            ? styles["singlePerson__background-video"]
            : styles["singlePerson__background-video-hide"]
        }
        src={Video}
        autoPlay
        muted
        loop
        playsInline
      >
        <img src={posterImage} />
      </video>
      {isLoading ? (
        <LoadingSpinner space={1} />
      ) : personData && id ? ( // personData.id == idではメンバープロフィールに飛んだ際にidに差異がある事から404になる
        <FetchContainer person={personData} />
      ) : (
        <Navigate to="/404" replace />
      )}
    </main>
  );
};

export default SinglePersonContainer;
